import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { BreadcrumbsService } from '../../core/breadcrumbs.service';
import { ScrollService } from '../../core/scroll.service';
import { LangService } from '../../core/lang.service';
import { Subscription } from 'rxjs';
import { ChecklistStep, ChecklistStepRoute } from '../pnel-cts-checklist/model/checklist-steps';
import { FormControl, FormGroup } from '@angular/forms';
import { IAccountType } from '../../constants/account-types';
import { LoginGuardService } from '../../api/login-guard.service';
import { AuthService } from '../../api/auth.service';
import { RoutesService } from '../../api/routes.service';
import { DB_MAX_LOC_SETUP } from '../../api/models/db-limits';
import { ITestSessionSetupLocations } from '../../api/models/db/test-session-setup-locations.schema';
import { MyTestSessionsSetupService, ITestSessionSetupInfo, ILocation } from '../my-test-sessions-setup.service';
import { MyInstitutionService, IInstitutionInfo } from '../my-institution.service';



@Component({
  selector: 'view-cts-location',
  templateUrl: './view-cts-location.component.html',
  styleUrls: ['./view-cts-location.component.scss']
})
export class ViewCtsLocationComponent implements OnInit, OnDestroy {

  public breadcrumb = [];
  public setupId:string;

  public locations:FormGroup[] = [];
  isLoaded:boolean;
  isLoadFailed:boolean;

  private routeSub:Subscription;
  private newLocations = new Map();
  private savingLocations = new Map();

  constructor(
    private loginGuard: LoginGuardService, // 
    private breadcrumbsService: BreadcrumbsService,
    private router:Router,
    private route:ActivatedRoute,
    private scrollService:ScrollService,
    private lang:LangService,
    private auth:AuthService,
    private routes:RoutesService,
    private mySetup:MyTestSessionsSetupService,
    private myInst:MyInstitutionService,
  ) { }
  
  ngOnInit() {
    this.loginGuard.activate([IAccountType.TEST_ADMIN]);
    this.scrollService.scrollToTop();
    this.routeSub = this.route.params.subscribe(routeParams => {
      this.setupId = routeParams['setupId'];
      this.breadcrumb = this.mySetup.generateBreadcrumbs(this.setupId, 'cts_location_title');
      this.initRouteView();    
    });
  }

  ngOnDestroy(){
    if (this.myInstSub){this.myInstSub.unsubscribe();}
  }

  mySetupSub:Subscription
  setupInfo:ITestSessionSetupInfo;
  myInstSub:Subscription
  instInfo:IInstitutionInfo;

  initRouteView(){
    // console.log('initRouteView')
    this.myInstSub = this.myInst.sub().subscribe(instInfo => {
      // console.log('initRouteView >> ', instInfo)
      if (instInfo){
        this.instInfo = instInfo;
        this.initLocations();
      }
    })
  }
  
  initLocations(){
    this.mySetup
      .getLocations(<any>this.setupId)
      .then(locationsData => {
        this.isLoaded = true;
        this.ingestLocations(locationsData);
        this.ensureDefaultFirstForm();
      })
      .catch(e => {
        this.isLoadFailed = true;
      })
  }

  private ingestLocations(locationsData: ILocation[]){
    this.locations = [];
    locationsData.forEach(locationData => {
      const newLocation = this.createLocationForm(locationData.id, locationData);
      newLocation.disable();
      this.locations.push(newLocation);
    })
  }

  private ensureDefaultFirstForm(){
    if (this.locations.length === 0){
      this.newLocation();
    }
  }

  private formProps = [
    // 'id', // handle this specially
    'room',
    'campusBuilding',
    'capacity',
    'address',
    'phoneNumber',
    'city',
    'province',
    'postalCode',
  ]

  private createLocationForm(id:number, data:ILocation){
    const formGroupConfig:any = {};
    formGroupConfig.id = new FormControl(id);
    formGroupConfig.isRemoved = new FormControl(data.isRemoved);
    this.formProps.forEach(prop => {
      formGroupConfig[prop] = new FormControl(data[prop]);
    })
    return new FormGroup(formGroupConfig);
  }

  private extractLocationData(location:FormGroup){
    const data:Partial<ILocation> = {};
    data.id = location.controls.id.value;
    this.formProps.forEach(prop => {
      data[prop] = location.controls[prop].value;
    })
    return data;
  }

  isLocFormValid(location:FormGroup){
    let hasAnyErrors = false;
    this.formProps.forEach(prop => {
      let errors:any = {};
      const control = location.controls[prop]
      if (! control.value){
        hasAnyErrors = true;
        errors.isBlank = true;
      }
      if (prop === 'capacity'){
        if (control.value <= 0){
          hasAnyErrors = true;
          errors.isInvalid = true;
        }
      }
      // console.log(prop, errors)
      control.setErrors(errors);
      control.markAsPristine();
    })
    return hasAnyErrors;
  }

  editSave(location:FormGroup){
    if (location.disabled || this.isSavingLocation(location) ){ 
      return; 
    }
    this.savingLocations.set(location, true);
    const hasAnyErrors = this.isLocFormValid(location);
    if (!hasAnyErrors){
      const formData = this.extractLocationData(location);
      const isNewLocation = !!this.newLocations.get(location);
      if (isNewLocation){
        this.mySetup
          .createLocation(formData)
          .then(res => {
            this.newLocations.set(location, false);
            this.savingLocations.set(location, false);
            location.disable(); 
          })
          .catch (e => {
            this.savingLocations.set(location, false);
          })
      }
      else{
        const locationId = formData.id;
        this.mySetup
          .patchLocation(locationId, formData)
          .then(res => {
            location.disable(); 
            this.savingLocations.set(location, false);
          })
          .catch( e => {
            this.savingLocations.set(location, false);
          })
      }
    }
  }

  isSavingLocation(location:FormGroup){
    return this.savingLocations.get(location);
  }

  isInputError(control:FormControl){
    if (control.pristine && ( control.hasError('isBlank') || control.hasError('isInvalid') ) ){
      return true;
    }
  }

  editCancel(location:FormGroup){
    if (this.newLocations.get(location)){
      const i = this.locations.indexOf(location);
      if (confirm(this.lang.tra('cts_location_cancel_rm_confirm')+(i+1))){
        this.locations.splice(i, 1);
        this.newLocations.set(location, false)
      }
    }
    else{
      location.disable();
    }
  }

  editStart(location:FormGroup){
    location.enable();
  }

  editRemove(location:FormGroup){
    const i = this.locations.indexOf(location);
    if (confirm(this.lang.tra('cts_location_rm_confirm')+(i+1))){
      const locationId = location.controls.id.value;
      this.mySetup
        .removeLocation(locationId)
        .then(res => {
          location.controls.isRemoved.setValue(true);
        })
        .catch (e => {
          if (e.message === 'HAS_LOCATIONS'){
            alert(this.lang.tra('cts_location_contains_timeslots_err_alert'))
          }
        })
    }
  }

  newLocation(){
    if (this.locations.length >= DB_MAX_LOC_SETUP){
      alert(this.lang.tra('alert_loc_setup_max_reached'))
    }
    const newLocation = this.createLocationForm(null, {
      room: '',
      campusBuilding: '',
      capacity: 0,
      address: this.instInfo.address,
      phoneNumber: this.instInfo.phoneNumber,
      city: this.instInfo.city,
      province: this.instInfo.province,
      postalCode: this.instInfo.postalCode,
    });
    this.newLocations.set(newLocation, true);
    this.locations.push(newLocation);
  }

  isReadyToContinue(){
    return this.mySetup.getChecklistStepState(ChecklistStepRoute.Location)
  }

  getCurrentStepId(){
    return ChecklistStep.Location
  }

  gotoNextRoute(){
    const nextRouteSuffix = ChecklistStepRoute.TechnicalReadiness;
    let breadcrumb = this.breadcrumbsService.TESTADMIN_CREATE_TEST_SESSION(this.setupId, nextRouteSuffix);
    this.router.navigate([ breadcrumb.route ]);
  }

}
